import { InfiniteData } from 'react-query';
import { ApiCollectionResponse } from '../types/api_response';
import AuthUtil from 'utils/AuthUtil';

export type Environment = 'dev' | 'int' | 'qa' | 'stage' | 'uat' | 'prod';

// Environment order from lowest to highest
export const ENV_ORDER: Environment[] = ['dev', 'int', 'qa', 'stage', 'uat', 'prod'];

export const getDomainPattern = () => {
  if (window.location.host.includes('localhost')) {
    return 'local';
  }

  if (window.location.host.includes('portal.')) {
    return 'old';
  }

  if (window.location.host.includes('app.lexipol.com')) {
    return 'new';
  }

  return 'new';
};

export const getEnv = (): Environment => {
  if (window.location.host.includes('localhost')) {
    return 'dev';
  }

  // First try the new app.lexipol.com pattern
  const newMatches = /(qa|int|dev|stage|uat).app.lexipol.com/gm.exec(window.location.host);
  if (newMatches && newMatches.length > 1) {
    return newMatches[1] as Environment;
  }

  // Fall back to the old portal.dev.phoenix.lexipol.com pattern
  const oldMatches = /portal\.(qa|int|dev|stage|uat)\.phoenix\.lexipol\.com/gm.exec(window.location.host);
  if (oldMatches && oldMatches.length > 1) {
    return oldMatches[1] as Environment;
  }

  return 'prod';
};

// Helper function to check if current environment is within maxEnv
export const isEnvironmentAllowed = (maxEnv: Environment): boolean => {
  const currentEnv = getEnv();
  const currentIndex = ENV_ORDER.indexOf(currentEnv);
  const maxIndex = ENV_ORDER.indexOf(maxEnv);
  return currentIndex <= maxIndex;
};

export const isFeatureEnabled = (feature: string) => {
  const env = getEnv();
  if (env === 'dev' || env === 'qa') return true;
  return false;
};

export const baseApiUrl = () => {
  const env = getEnv();
  const domainPattern = getDomainPattern();

  // For local development, use the stored API URL or default
  if (domainPattern === 'local') {
    return localStorage.getItem('apiBaseUrl') || process.env.REACT_APP_API_BASE_URL_DEV;
  }

  // Get the base API URL based on environment
  let apiUrl = '';
  switch (env) {
    case 'qa':
      apiUrl = process.env.REACT_APP_API_BASE_URL_QA as string;
      break;
    case 'dev':
      apiUrl = process.env.REACT_APP_API_BASE_URL_DEV as string;
      break;
    case 'int':
      apiUrl = process.env.REACT_APP_API_BASE_URL_INT as string;
      break;
    case 'uat':
      apiUrl = process.env.REACT_APP_API_BASE_URL_UAT as string;
      break;
    case 'stage':
      apiUrl = process.env.REACT_APP_API_BASE_URL_STAGE as string;
      break;
    case 'prod':
      apiUrl = process.env.REACT_APP_API_BASE_URL_PROD as string;
      break;
    default:
      apiUrl = '';
      break;
  }

  // For old domain pattern, transform the URL to use .phoenix. instead of .app.
  if (domainPattern === 'old' && apiUrl) {
    return apiUrl.includes('.app.') ? apiUrl.replace('.app.', '.phoenix.') : apiUrl;
  }

  return apiUrl;
};

export const lexAiWebSocketBaseUrl = () => {
  const env = getEnv();
  const domainPattern = getDomainPattern();

  if (domainPattern === 'local') {
    return (
      localStorage.getItem('lexAiWebSocketBaseUrl') ||
      (process.env.REACT_APP_LEX_AI_WEBSOCKET_BASE_URL_DEV as string)
    );
  }

  let wsUrl = '';
  switch (env) {
    case 'qa':
      wsUrl = process.env.REACT_APP_LEX_AI_WEBSOCKET_BASE_URL_QA as string;
      break;
    case 'dev':
      wsUrl = process.env.REACT_APP_LEX_AI_WEBSOCKET_BASE_URL_DEV as string;
      break;
    case 'int':
      wsUrl = process.env.REACT_APP_LEX_AI_WEBSOCKET_BASE_URL_INT as string;
      break;
    case 'uat':
      wsUrl = process.env.REACT_APP_LEX_AI_WEBSOCKET_BASE_URL_UAT as string;
      break;
    case 'stage':
      wsUrl = process.env.REACT_APP_LEX_AI_WEBSOCKET_BASE_URL_STAGE as string;
      break;
    case 'prod':
      wsUrl = process.env.REACT_APP_LEX_AI_WEBSOCKET_BASE_URL_PROD as string;
      break;
    default:
      wsUrl = '';
      break;
  }

  if (domainPattern === 'old' && wsUrl) {
    return wsUrl.includes('.app.') ? wsUrl.replace('.app.', '.phoenix.') : wsUrl;
  }

  return wsUrl;
};

export const performanceReportingApiUrl = () => {
  const domainPattern = getDomainPattern();

  // For local development, check for localStorage override
  if (domainPattern === 'local') {
    return localStorage.getItem('performanceReportingApiBaseUrl') || baseApiUrl();
  }

  // For all other environments, use the standard API URL
  return baseApiUrl();
};

export const endUserAppUrl = () => {
  const env = getEnv();
  const domainPattern = getDomainPattern();
  let appUrl = '';

  // For local development, return empty
  if (domainPattern === 'local') {
    return '/dashboard';
  }

  // For both old and new domains, use the environment-specific end user URL
  switch (env) {
    case 'qa':
      appUrl = process.env.REACT_APP_END_USER_BASE_URL_QA || '';
      break;
    case 'dev':
      appUrl = process.env.REACT_APP_END_USER_BASE_URL_DEV || '';
      break;
    case 'int':
      appUrl = process.env.REACT_APP_END_USER_BASE_URL_INT || '';
      break;
    case 'uat':
      appUrl = process.env.REACT_APP_END_USER_BASE_URL_UAT || '';
      break;
    case 'stage':
      appUrl = process.env.REACT_APP_END_USER_BASE_URL_STAGE || '';
      break;
    case 'prod':
      appUrl = process.env.REACT_APP_END_USER_BASE_URL_PROD || '';
      break;
    default:
      appUrl = '';
  }

  return `${domainPattern === 'old' ? appUrl.replace('app.', 'phoenix.') : appUrl}/dashboard`;
};

export const baseAppUrl = () => {
  const env = getEnv();
  const domainPattern = getDomainPattern();
  let appUrl = '';

  // Handle local development
  if (domainPattern === 'local') {
    return window.location.origin;
  }

  // Handle old domain pattern
  if (domainPattern === 'old') {
    switch (env) {
      case 'qa':
        appUrl = 'https://portal.qa.phoenix.lexipol.com';
        break;
      case 'dev':
        appUrl = 'https://portal.dev.phoenix.lexipol.com';
        break;
      case 'int':
        appUrl = 'https://portal.int.phoenix.lexipol.com';
        break;
      case 'uat':
        appUrl = 'https://portal.uat.phoenix.lexipol.com';
        break;
      case 'stage':
        appUrl = 'https://portal.stage.phoenix.lexipol.com';
        break;
      case 'prod':
        appUrl = 'https://portal.phoenix.lexipol.com';
        break;
      default:
        appUrl = '';
    }
  }

  // Handle new domain pattern
  if (domainPattern === 'new') {
    switch (env) {
      case 'qa':
        appUrl = process.env.REACT_APP_END_USER_BASE_URL_QA || '';
        break;
      case 'dev':
        appUrl = process.env.REACT_APP_END_USER_BASE_URL_DEV || '';
        break;
      case 'int':
        appUrl = process.env.REACT_APP_END_USER_BASE_URL_INT || '';
        break;
      case 'uat':
        appUrl = process.env.REACT_APP_END_USER_BASE_URL_UAT || '';
        break;
      case 'stage':
        appUrl = process.env.REACT_APP_END_USER_BASE_URL_STAGE || '';
        break;
      case 'prod':
        appUrl = process.env.REACT_APP_END_USER_BASE_URL_PROD || '';
        break;
      default:
        appUrl = '';
    }
  }

  // Fallback for production
  return appUrl;
};

export const defaultHeaders: (options?: any) => HeadersInit = (options?: { forceError?: boolean }) =>
  ({
    'X-Api-Key': options?.forceError ? 'xx' : process.env.REACT_APP_API_GATEWAY_KEY,
    'Content-Type': 'application/json',
    Authorization: AuthUtil.getToken(),
  }) as HeadersInit;

export const getToken = () => AuthUtil.getToken();

export const queryDefaults = {
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 1000 * 60 * 2, // 2 min
      cacheTime: 1000 * 60 * 5, // 5 min
      keepPreviousData: true,
    },
  },
};

export type FetcherArgs = {
  url: string;
  method?: string;
  body?: any;
  options?: RequestInit;
  anonymousAllowed?: boolean;
};
export const fetcher = ({
  url,
  method = 'get',
  body,
  options = {},
  anonymousAllowed = false,
}: FetcherArgs): Promise<any> => {
  const sessionExpired: boolean = AuthUtil.isTokenExpired();
  // console.log('fetcher', anonymousAllowed, sessionExpired);
  if (!anonymousAllowed && sessionExpired) {
    console.warn('sessionExpired:reject');
    // return Promise.resolve(null);
  }

  const defaultHdrs = defaultHeaders() as Record<string, string>;
  const apiKey = defaultHdrs['X-Api-Key'];
  const contentType = defaultHdrs['Content-Type'];
  const headers = {
    ...(anonymousAllowed
      ? {
          'X-Api-Key': apiKey,
          'Content-Type': contentType,
        }
      : defaultHdrs),
    ...(options.headers || {}),
  };

  const requestConfig = {
    method: method.toUpperCase(),
    ...(method !== 'get' && { body: JSON.stringify(body) }),
    ...options,
    headers,
  };

  return fetch(url, requestConfig)
    .then(async (r) => {
      if (!r.ok) {
        let errorData;
        try {
          errorData = await r.json();
        } catch (e) {
          errorData = { message: await r.text(), status: r.status, statusText: r.statusText };
        }
        throw new Error(JSON.stringify(errorData));
      }
      if (r.status === 204) {
        return true;
      } else {
        const data = await r.json();
        return data;
      }
    })
    .catch((error) => {
      throw error;
    });
};

export const blobFetcher = ({
  url,
  method = 'get',
  body,
  options = {},
  anonymousAllowed = false,
}: FetcherArgs): Promise<any> => {
  return fetch(url, {
    method,
    ...(method !== 'get' && { body: JSON.stringify(body) }),
    headers: defaultHeaders(),
    ...options,
  }).then((r) => r.blob());
};

export const emptyResults: ApiCollectionResponse<any> = {
  content: [],
  count: 0,
};

export const infiniteEmptyResults: InfiniteData<ApiCollectionResponse<any>> = {
  pages: [
    {
      content: [],
      count: 0,
    },
  ],
  pageParams: [],
};

export const testResponse: object = {
  data: [],
  error: {},
  isError: false,
  isFetched: true,
  isFetching: false,
  isInitialLoading: false,
  isLoading: false,
  isSuccess: true,
};

export type ServerError = {
  code: number;
  detail: string;
  message: string;
};

export type ServerErrorResponse = {
  detail?: string;
  errorDetails: ServerError[] | null;
  errorInformationCode?: number;
  errorType?: string;
  errorMessage?: string;
  message?: string;
  requestId?: string;
};

type ErrorConfig = {
  onError?: (errors: ServerErrorResponse) => void;
};

export const handleError = (err: Error, config: ErrorConfig = {}) => {
  let serverError = {
    message: 'An unexpected error occurred.',
  };

  try {
    const parsed = JSON.parse(err.message);
    serverError = {
      ...parsed,
      message: parsed.message || serverError.message,
    };
  } catch (parseError) {
    console.warn('Failed to parse error message as JSON:', parseError, err);
  }

  if (config.onError) {
    config.onError(serverError as ServerErrorResponse);
  }
};

export const createParamString = (params: Record<string, any>): string => {
  return new URLSearchParams(
    Object.entries(params)
      // eslint-disable-next-line no-unused-vars
      .filter(([_k, v]) => v != null)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
  ).toString();
};

/**
 * When the user is stepped up as an org (viewing in another org's context),
 * appends organizationId query param so the LMS opens in that org's context.
 */
const getSteppedUpOrganizationIdParam = (): string => {
  if (typeof window === 'undefined' || !window.localStorage) return '';
  const currentOrgId = localStorage.getItem('phx-currentOrganizationId');
  const userOrgId = localStorage.getItem('phx-userOrgId');
  if (!currentOrgId || currentOrgId === userOrgId) return '';
  return currentOrgId;
};

export const getLmsUrl = (path: string = ''): string => {
  const env = getEnv();

  // For production, use lms.lexipol.com without environment prefix
  let baseUrl: string;
  if (env === 'prod') {
    baseUrl = `https://lms.lexipol.com${path}`;
  } else {
    baseUrl = `https://${env}.lms.lexipol.com${path}`;
  }

  const organizationId = getSteppedUpOrganizationIdParam();
  if (!organizationId) return baseUrl;

  const separator = path.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}organizationId=${encodeURIComponent(organizationId)}`;
};
